<template>
  <!-- eslint-disable max-len -->
  <div class="configuration">
    <transition name="fade">
      <confirmation-modal
        v-if="showDeactivateConfirmation"
        :confirmation-type="deactivateConfirmation.type"
        :title="deactivateConfirmation.title"
        :description="deactivateConfirmation.description"
        :button-text="deactivateConfirmation.button"
        @confirm="handleDeactivate"
        @cancel="toggleDeactivateConfirmation"
      />
    </transition>
    <transition name="fade">
      <wellness-info-modal
        v-if="showWellnessInfoModal"
        @close="toggleWellnessInfoModal"
      />
    </transition>
    <div class="page-topbar">
      <div class="page-topbar__title">
        Authenticator Management
      </div>
    </div>
    <tab-nav :tabs="navItems" />
    <template v-if="fetching">
      <div class="empty-state">
        <loader :size="100" />
      </div>
    </template>
    <template v-else>
      <template v-if="!isBCIDActive">
        <callout type="newFeature">
          <template v-slot:image>
            <img :src="wellnessIcon" />
          </template>
          <template v-slot:title>
            Wellness Declaration
          </template>
          <template v-slot:message>
            Allow users to receive a simple configurable survey, once per day, to detect potential
            health risks before they enter your facility.
          </template>
          <template v-slot:actions>
            <button
              class="btn btn--grey mr-1"
              @click="toggleWellnessInfoModal()"
            >
              Learn More
            </button>
            <button
              class="btn mr-1"
              @click="toggleActivateMode(supportedAuthenticators[0])"
            >
              Set Up
            </button>
          </template>
        </callout>
      </template>
      <div class="authenticator">
        <div
          v-show="isDefaultSet"
          class="alert-message-bar"
        >
          <span
            class="mr-1"
            v-html="alertIcon"
          />
          You currently do not have a default authenticator to handle account-wide two-factor
          authentication. Users not in a group will be on bypass.
        </div>
        <div
          v-show="!isDefaultSet"
          class="authenticator-section"
        >
          <two-factor-schedule />
        </div>
        <div class="authenticator-section">
          <div class="authenticator-section-heading">
            <div class="authenticator-section-heading__title">
              Available Authenticators
            </div>
          </div>
          <template v-if="isUpdateMode && selectedAuthenticator">
            <div class="flex-grid">
              <div class="authenticator-box-container">
                <div class="authenticator-box authenticator-box--active column">
                  <div
                    v-show="isDefault(selectedAuthenticator.type)"
                    class="authenticator-box__default"
                  >
                    Default
                  </div>
                  <img
                    class="authenticator-box__logo"
                    :src="selectedAuthenticator.logo"
                  />
                  <span
                    v-show="selectedAuthenticator.label"
                    class="authenticator-box__label"
                  >
                    {{ selectedAuthenticator.label }}
                  </span>
                </div>
                <div
                  v-show="selectedAuthenticator.name"
                  class="authenticator-box__name"
                >
                  {{ selectedAuthenticator.name }}
                </div>
              </div>
              <component
                :is="selectedAuthenticator.component"
                class="column"
                :is-activate-mode="false"
                :parent-error="errorMessage"
                :loading="fetching"
                :selected-authenticator="selectedAuthenticator"
                :default-authenticator="defaultAuthenticator"
                @deactivate="toggleDeactivateConfirmation"
                @cancel="toggleUpdateMode(selectedAuthenticator)"
                @update-setting="updateAccountAuthenticator"
              >
              </component>
            </div>
          </template>
          <template v-else>
            <div class="authenticator-section-content">
              <template v-for="authenticator in savedAuthenticators">
                <div
                  :id="authenticator.type + '-authenticator'"
                  :key="authenticator.type"
                  class="authenticator-box-container"
                >
                  <div
                    v-tooltip="{ content: authenticator.name }"
                    class="authenticator-box authenticator-box--active"
                    @click="toggleUpdateMode(authenticator)"
                  >
                    <div
                      v-show="isDefault(authenticator.type)"
                      class="authenticator-box__default"
                    >
                      Default
                    </div>
                    <img
                      class="authenticator-box__logo"
                      :src="authenticator.logo"
                    />
                    <span
                      v-show="authenticator.label"
                      class="authenticator-box__label"
                    >
                      {{ authenticator.label }}
                    </span>
                  </div>
                  <div class="authenticator-box__name">
                    {{ authenticator.name }}
                  </div>
                </div>
              </template>
            </div>
          </template>
          <template v-if="savedAuthenticators.length === 0">
            <div class="authenticator-empty">
              <div class="authenticator-empty__title">
                Your system is currently not secured by second factor authentication.
              </div>
              <div class="authenticator-empty__description">
                If you would like to add this functionality, please select and configure one of the
                second factor authenticator providers below.
              </div>
            </div>
          </template>
        </div>
        <div class="authenticator-section">
          <div class="authenticator-section-heading">
            <div class="authenticator-section-heading__title">
              Supported Authenticators
            </div>
          </div>
          <template v-if="isActivateMode && selectedAuthenticator">
            <div class="flex-grid">
              <div class="authenticator-box authenticator-box--active column mr-1 ml-1">
                <img
                  class="authenticator-box__logo"
                  :src="selectedAuthenticator.logo"
                />
                <span
                  v-show="selectedAuthenticator.label"
                  class="authenticator-box__label"
                >
                  {{ selectedAuthenticator.label }}
                </span>
              </div>
              <component
                :is="selectedAuthenticator.component"
                class="column"
                :is-activate-mode="true"
                :parent-error="errorMessage"
                :loading="fetching"
                @create-credential="handleActivate"
                @update-setting="updateAccountAuthenticator"
                @cancel="toggleActivateMode(selectedAuthenticator)"
              >
              </component>
            </div>
          </template>
          <template v-else>
            <div class="authenticator-section-content">
              <template v-for="authenticator in availableAuthenticators">
                <div
                  :id="authenticator.type + '-authenticator'"
                  :key="authenticator.type"
                  class="authenticator-box-container"
                >
                  <div
                    class="authenticator-box"
                    @click="toggleActivateMode(authenticator)"
                  >
                    <img
                      class="authenticator-box__add"
                      :src="addIcon"
                    />
                    <img
                      class="authenticator-box__logo"
                      :src="authenticator.logo"
                    />
                    <span
                      v-show="authenticator.label"
                      class="authenticator-box__label"
                    >
                      {{ authenticator.label }}
                    </span>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
  <!-- eslint-enable max-len -->
</template>

<script>
//Libraries
import feather from 'feather-icons'
// Components
import Loader from '@/components/loader.vue'
import TabNav from '@/components/tabNav.vue'
import duoConfiguration from '@/components/authenticators/DUO.vue'
import bcidConfiguration from '@/components/authenticators/BCID.vue'
import smsConfiguration from '@/components/authenticators/SMS.vue'
import pingConfiguration from '@/components/authenticators/PING.vue'
import oktaConfiguration from '@/components/authenticators/OKTA.vue'
import confirmationModal from '@/components/confirmationModal.vue'
import WellnessInfoModal from '@/components/wellnessInfoModal.vue'
import Callout from '@/components/callout.vue'
import TwoFactorSchedule from '@/components/twoFactorSchedule.vue'
// Mixins
import configurationNavMixin from '@/mixins/configurationNavMixin'
// Queries
import settingQueries from '@/graphql/settings.js'
import credentialQueries from '@/graphql/credentials.js'
// Assets
import addIcon from '@/assets/add-icon.svg'
import duoLogo from '@/assets/duoLogo.png'
import pingLogo from '@/assets/pingLogo.png'
import bcidLogo from '@/assets/b-logo.svg'
import smsLogo from '@/assets/smsLogo.svg'
import oktaLogo from '@/assets/oktaLogo.png'
import wellnessIcon from '@/assets/health-check.svg'
import {ENV} from '@/config'

export default {
  name: 'AuthenticatorManagement',
  components: {
    Loader,
    TabNav,
    duoConfiguration,
    bcidConfiguration,
    smsConfiguration,
    pingConfiguration,
    oktaConfiguration,
    confirmationModal,
    WellnessInfoModal,
    Callout,
    TwoFactorSchedule,
  },
  mixins: [configurationNavMixin, settingQueries, credentialQueries],
  data() {
    return {
      fetching: false,
      isActivateMode: false,
      isUpdateMode: false,
      showDeactivateConfirmation: false,
      showWellnessInfoModal: false,
      defaultAuthenticator: null,
      credentials: null,
      errorMessage: '',
      selectedAuthenticator: {},
      deactivateConfirmation: {
        type: 'danger',
        title: 'Delete authenticator?',
        description:
          'This authenticator will no longer be active. Credentials related to this authenticator will be deleted.',
        button: 'Delete',
      },
      supportedAuthenticators: [
        {
          type: 'bcid',
          component: bcidConfiguration,
          logo: bcidLogo,
        },
        {
          type: 'duo',
          component: duoConfiguration,
          logo: duoLogo,
        },
        {
          type: 'ping',
          component: pingConfiguration,
          logo: pingLogo,
        },
        {
          type: 'okta',
          component: oktaConfiguration,
          logo: oktaLogo,
        },
        {
          type: 'sms',
          component: smsConfiguration,
          logo: smsLogo,
          label: 'BETA',
        },
      ],
      addIcon: addIcon,
      alertIcon: feather.icons['alert-circle'].toSvg(),
      wellnessIcon: wellnessIcon,
    }
  },
  computed: {
    isDefaultSet() {
      return (
        this.defaultAuthenticator.authenticator === 'none' && this.savedAuthenticators.length > 0
      )
    },
    isDevOrQAEnv() {
      return ENV.environment === 'development' || ENV.environment === 'qa'
    },
    isBCIDActive() {
      let _isActive = false
      if (this.credentials) {
        _isActive = this.credentials.some((credential) => {
          return credential.type === 'bcid'
        })
      }
      return _isActive
    },
    /*
     * Authenticators that are in listCredentials, and any authenticators that
     * are set as the account authenticator, but do not have a credential (SMS)
     */
    savedAuthenticators() {
      const _savedAuthenticators = []
      const _smsAuthenticatorDetails = this.supportedAuthenticators.find((authenticator) => {
        return authenticator.type === 'sms'
      })
      if (this.defaultAuthenticator.authenticator === 'sms') {
        _savedAuthenticators.push({
          ...this.defaultAuthenticator,
          ..._smsAuthenticatorDetails,
        })
      }
      this.credentials.forEach((savedCred) => {
        this.supportedAuthenticators.forEach((supportedCred) => {
          if (savedCred.type === supportedCred.type) {
            _savedAuthenticators.push({
              ...savedCred,
              ...supportedCred,
            })
          }
        })
      })
      return _savedAuthenticators
    },
    availableAuthenticators() {
      const _isSMSActive = this.defaultAuthenticator.authenticator === 'sms'
      return this.supportedAuthenticators.filter((authenticator) => {
        if (this.isBCIDActive && authenticator.type === 'bcid') {
          return
        }
        if (_isSMSActive && authenticator.type === 'sms') {
          return
        }
        return authenticator
      })
    },
  },
  created() {
    this.fetchSettings()
  },
  methods: {
    async fetchSettings() {
      this.fetching = true
      try {
        this.defaultAuthenticator = await this.listSettingsQuery()
        const {credentialsList} = await this.listCredentialsQuery({
          page: 1,
          perPage: 10,
        })
        this.fetching = false
        this.credentials = credentialsList
      } catch (error) {
        this.fetching = false
        this.displayAlert('fail', 'Unable to fetch setting.')
        console.log(error)
      }
    },
    async updateAccountAuthenticator(accountSetting) {
      this.fetching = true
      try {
        await this.updateSettingsMutation(accountSetting)
        this.fetching = false
        this.displayAlert('success', 'Authenticator updated.')
        if (this.isUpdateMode) {
          this.toggleUpdateMode()
        }
        if (this.isActivateMode) {
          this.toggleActivateMode()
        }
        this.fetchSettings()
      } catch (error) {
        this.fetching = false
        this.displayAlert('fail', 'Unable to update authenticator mode.')
        console.log(error)
      }
    },
    async deleteCredential(uuid) {
      this.fetching = true
      try {
        if (this.selectedAuthenticator.type === 'bcid') {
          throw new Error('Cannot Delete BCID')
        } else {
          const _result = await this.deleteCredentialMutation(uuid)
          this.fetching = false
          return _result
        }
      } catch (error) {
        this.fetching = false
        console.log(error)
        throw new Error('Unable to delete credential.')
      }
    },
    /*
     * A credential must be created before a authenticator is set.
     */
    async createCredential(credential) {
      this.fetching = true
      try {
        const {uuid} = await this.createCredentialMutation(credential)
        this.fetching = false
        return uuid
      } catch (error) {
        this.fetching = false
        this.errorMessage = error.message
        if (this.errorMessage.includes('credential already exists')) {
          this.displayAlert('fail', 'The credentials you\'ve entered already exist.')
        } else {
          this.displayAlert('fail', 'Unable to validate configuration.')
        }
        console.log(error)
      }
    },
    async handleDeactivate() {
      if (this.showDeactivateConfirmation) {
        this.toggleDeactivateConfirmation()
      }
      try {
        const {uuid, type} = this.selectedAuthenticator
        if (uuid) {
          await this.deleteCredential(uuid)
        }
        this.fetchSettings()
        this.toggleUpdateMode()
      } catch (error) {
        const {message} = error
        if (message.includes('group')) {
          this.displayAlert('fail', 'Unable to delete, authenticator is added to a group.')
        } else {
          this.displayAlert('fail', 'Unable to delete authenticator.')
        }
        console.log('Unable to Deactivate Setting', error)
      }
    },
    /*
     * If there are credentials to be created, wait for them to be created,
     * then activate the setting. A credential needs to be created before
     * the setting can be set. If the user has selected to set this
     * authenticator as their default, then update the account settings.
     */
    async handleActivate(payload) {
      this.errorMessage = ''
      let _uuid = null
      try {
        if (payload.credential) {
          _uuid = await this.createCredential(payload.credential)
        }
        if (payload.setDefault) {
          const _accountSettingPayload = {
            authenticator: payload.credential.type,
            credentialUUID: _uuid,
          }
          await this.updateAccountAuthenticator(_accountSettingPayload)
        } else {
          this.fetchSettings()
        }
        this.toggleActivateMode()
      } catch (error) {
        console.log('Unable to Activate', error)
      }
    },
    isDefault(authenticatorType) {
      return this.defaultAuthenticator.authenticator === authenticatorType
    },
    toggleActivateMode(authenticator) {
      if (this.isUpdateMode) {
        this.toggleUpdateMode(authenticator)
      }
      this.isActivateMode = !this.isActivateMode
      this.selectedAuthenticator = this.isActivateMode ? authenticator : {}
    },
    toggleUpdateMode(authenticator) {
      if (this.isActivateMode) {
        this.toggleActivateMode(authenticator)
      }
      this.isUpdateMode = !this.isUpdateMode
      this.selectedAuthenticator = this.isUpdateMode ? authenticator : {}
    },
    toggleDeactivateConfirmation() {
      this.showDeactivateConfirmation = !this.showDeactivateConfirmation
    },
    toggleWellnessInfoModal() {
      this.showWellnessInfoModal = !this.showWellnessInfoModal
    },
  },
}
</script>

<style lang="scss" scoped>
$box-size: 150px;
.authenticator {
  color: $dark-grey;
  &-section {
    padding: 1em;
    border: 1px solid $light-grey;
    border-radius: 5px;
    box-sizing: border-box;
    margin: 1em 0;
    &-heading {
      @include flex-display();
      @include flex-justify-content(space-between);
      @include flex-align-items(center);
      margin-bottom: 2em;
      &__title {
        font-size: $large-font;
        font-weight: 600;
      }
    }
    &-content {
      @include flex-display();
      flex-wrap: wrap;
      &--selected {
        @include flex-direction(column);
        @include flex-justify-content(space-between);
      }
      &-footer {
        @include flex-display();
        @include flex-justify-content(flex-end);
        @include flex-align-items(flex-end);
        width: 100%;
      }
    }
  }
  &-empty {
    @include flex-display();
    @include flex-justify-content(center);
    @include flex-align-items(center);
    @include flex-direction(column);
    box-sizing: border-box;
    text-align: center;
    width: 100%;
    height: 200px;
    border: 2px solid $red;
    border-radius: 5px;
    background: lighten($red, 41%);
    &__title {
      font-weight: 600;
      font-size: $large-font;
      margin-bottom: 1em;
    }
    &__description {
      font-size: $small-font;
      line-height: 1.5em;
      width: 30%;
    }
  }
  &-box {
    @include flex-display();
    @include flex-align-items(center);
    @include flex-justify-content(center);
    position: relative;
    width: $box-size;
    height: $box-size;
    max-width: $box-size;
    max-height: $box-size;
    border-radius: 5px;
    padding: 1em;
    box-sizing: border-box;
    background-color: $off-white;
    background-origin: content-box;
    background-repeat: no-repeat;
    background-position: center;
    color: $grey-text;
    font-weight: 600;
    font-size: $large-font;
    text-transform: uppercase;
    &:hover {
      cursor: pointer;
      .authenticator-box__add {
        opacity: 1;
      }
      .authenticator-box__logo {
        opacity: 0;
      }
    }
    &-container {
      position: relative;
      margin: 0 1em 1em;
    }
    &__name {
      margin-top: 0.5em;
      max-width: $box-size;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      text-align: center;
      font-size: $small-font;
    }
    &__label {
      position: absolute;
      top: 0.5em;
      right: 0.5em;
      font-size: $tiny-font;
      color: $white;
      border-radius: 5px;
      background: $black;
      padding: 5px;
    }
    &__add {
      position: absolute;
      opacity: 0;
      transition: opacity 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    }
    &__logo {
      position: absolute;
      max-width: 100px;
      transition: opacity 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    }
    &--active {
      &:hover {
        cursor: pointer;
        .authenticator-box__add {
          opacity: 0;
        }
        .authenticator-box__logo {
          opacity: 1;
        }
      }
    }
    &__default {
      position: absolute;
      bottom: 0;
      width: 100%;
      padding: 0.3em 0;
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;
      background: $blue;
      color: $white;
      text-align: center;
      font-size: $tiny-font;
      text-transform: none;
    }
  }
}
</style>
